import { useEffect, useState } from "react";

type IdleWindow = Window & {
  requestIdleCallback?: (cb: (deadline: { timeRemaining: () => number }) => void, opts?: { timeout: number }) => number;
  cancelIdleCallback?: (id: number) => void;
};

/**
 * Progressively reveals `stageCount` stages, scheduling each in its own
 * `requestIdleCallback` (or `setTimeout` fallback) so each batch of work
 * runs in a separate task. This keeps every individual stage well under
 * the 50 ms long-task threshold and lets the browser interleave rendering,
 * input, and layout work between them.
 *
 * @param stageCount  Total number of stages to reveal.
 * @param gapMs       Minimum gap between stage advancements (default 80ms).
 * @param idleTimeout Hard cap before forcing the next stage (default 1500ms).
 * @returns           Current stage index (0..stageCount). A widget should
 *                    render when `stage > itsIndex`.
 */
export function useStagedMount(stageCount: number, gapMs = 80, idleTimeout = 1500): number {
  const [stage, setStage] = useState(0);

  useEffect(() => {
    if (stage >= stageCount) return;
    const w = window as IdleWindow;
    let idleId: number | undefined;
    let timeoutId: number | undefined;
    let cancelled = false;

    const advance = () => {
      if (cancelled) return;
      setStage((s) => Math.min(s + 1, stageCount));
    };

    const schedule = () => {
      if (typeof w.requestIdleCallback === "function") {
        idleId = w.requestIdleCallback(advance, { timeout: idleTimeout });
      } else {
        timeoutId = window.setTimeout(advance, gapMs);
      }
    };

    // Wait `gapMs` between stages so the previous stage's render/commit
    // has a chance to settle before queueing the next idle task.
    timeoutId = window.setTimeout(schedule, gapMs);

    return () => {
      cancelled = true;
      if (idleId !== undefined) w.cancelIdleCallback?.(idleId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [stage, stageCount, gapMs, idleTimeout]);

  return stage;
}
